// Het e-mailadres van één huishouden, uit de per-omgeving instelbare
// gegevensvelden (`AdressData`).
//
// WAAROM NIET `resolvePrimaryFieldValue`. Die plakt alle gevonden waarden aan
// elkaar met een spatie. Voor 'Voornaam Achternaam' is dat precies de bedoeling;
// voor een e-mailadres levert het `a@b.nl c@d.nl` op — geen adres meer, en de
// verzending faalt of gaat naar niemand. Dezelfde helper hergebruiken omdat hij
// 'hetzelfde doet' is hier lossy.
// AI-CONTEXT: See docs/pitfalls.md#dev-helper-lossy-on-input-it-claims-to-accept
//
// `Tasks.jsx` had deze veldnamen ingelijnd staan, terwijl het commentaar bij
// `HOUSEHOLD_NAME_FIELD` er letterlijk voor waarschuwt. Nu één lijst.
// AI-CONTEXT: See docs/pitfalls.md#dev-copied-recipe-instead-of-shared-hook

import normalizeEmail from '../normalizeEmail'
import { isSentinel } from '../sentinelValue'

/**
 * De titels waaronder een e-mailadres in `AdressData` kan staan, op volgorde
 * van voorkeur. Omgevingen stellen hun eigen veldnamen in, dus dit is een
 * lijst en geen enkele naam.
 */
const HOUSEHOLD_EMAIL_TITLES = ['E-mail', 'Email', 'E-mailadres', 'Emailadres']

/**
 * Minimale vorm-controle. Geen volledige RFC-toets: het doel is voorkomen dat
 * er onzin de verzendwachtrij in gaat, niet elk exotisch adres afkeuren.
 *
 * '' TELT HIER ALS SENTINEL, en dat deed het al: de oude lijst had '' er niet
 * in staan, maar de guard begon met `!value`. Een leeg 'E-mail'-veld is geen
 * adres, en de lus hieronder loopt door naar de volgende titel.
 * AI-CONTEXT: See docs/pitfalls.md#database-url-sentinel-string-persisted
 *
 * @param {string} value - Al door `normalizeEmail` gehaald: getrimd en lowercase
 * @returns {boolean}
 */
const looksLikeEmail = (value) => {
    if (isSentinel(value)) return false
    const [gebruiker, domein, ...rest] = value.split('@')
    if (rest.length > 0) return false
    return Boolean(gebruiker) && Boolean(domein) && domein.includes('.')
}

/**
 * @param {Array<{Title?: string, Value?: string}>} [dataRows] - `AdressData` van
 *   dit huishouden. `undefined` betekent 'nog niet geladen' — bewust iets
 *   anders dan een lege lijst.
 * @returns {string|null} Het genormaliseerde adres, '' als er geen bruikbaar
 *   adres is, of null zolang de gegevens niet geladen zijn.
 */
const resolveHouseholdEmail = (dataRows) => {
    if (!Array.isArray(dataRows)) return null

    // ALLE waarden per titel bewaren, niet alleen de eerste: dezelfde titel kan
    // meermaals voorkomen, en dan mag een lege rij een gevulde niet wegdrukken.
    const perTitel = new Map()
    for (const row of dataRows) {
        if (!row?.Title) continue
        const lijst = perTitel.get(row.Title) || []
        lijst.push(row.Value)
        perTitel.set(row.Title, lijst)
    }

    for (const titel of HOUSEHOLD_EMAIL_TITLES) {
        for (const waarde of perTitel.get(titel) || []) {
            const kandidaat = normalizeEmail(waarde)
            // Onbruikbaar? Doorlopen in plaats van opgeven — een omgeving kan
            // 'E-mail' leeg hebben staan en 'Email' gevuld.
            if (looksLikeEmail(kandidaat)) return kandidaat
        }
    }

    return ''
}

export default resolveHouseholdEmail
export { HOUSEHOLD_EMAIL_TITLES, looksLikeEmail }
